import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { unstable_useBlocker as useBlocker } from 'react-router-dom'

import { F0ActionBar, F0ActionBarRef } from '@factorialco/f0-react/dist/experimental'
import { ActionBarStatus } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { QuestionnaireType } from './types'

function hasEmptyTitles(questionnaire: QuestionnaireType): boolean {
  return questionnaire.elements.some((element) => {
    if (!element.label.trim()) return true
    if (element.type === 'section') {
      return element.elements.some((question) => !question.label.trim())
    }
    return false
  })
}

interface ActionBarState {
  label: string
  status?: ActionBarStatus
}

function getDefaultState(): ActionBarState {
  return { label: i18n.t('forms.action_bar.label') }
}

interface FormEditorActionsProps {
  isDirty: boolean
  questionnaire: QuestionnaireType
  onSave: () => Promise<boolean>
  onDiscard: () => void
  isThinking?: boolean
}

export const FormEditorActions = ({
  isDirty,
  questionnaire,
  onSave,
  onDiscard,
  isThinking,
}: FormEditorActionsProps) => {
  const actionBarRef = useRef<F0ActionBarRef>(null)
  const [barState, setBarState] = useState<ActionBarState>(getDefaultState)

  const blocker = useBlocker(isDirty)

  const hasValidationErrors = useMemo(() => hasEmptyTitles(questionnaire), [questionnaire])

  useEffect(() => {
    if (barState.status === 'error' && !hasValidationErrors) {
      setBarState(getDefaultState())
    }
  }, [hasValidationErrors, barState.status])

  const handleSave = useCallback(async () => {
    if (hasValidationErrors) {
      setBarState({ label: i18n.t('forms.action_bar.empty_title_error'), status: 'error' })
      actionBarRef.current?.wiggle({ errorHighlight: true })
      return
    }
    setBarState({ label: i18n.t('forms.action_bar.label'), status: 'loading' })
    const success = await onSave()
    if (success) {
      setBarState({ label: i18n.t('forms.action_bar.saved'), status: 'success' })
      setTimeout(() => setBarState(getDefaultState()), 1500)
    } else {
      setBarState(getDefaultState())
    }
  }, [hasValidationErrors, onSave])

  const handleDiscard = useCallback(() => {
    setBarState(getDefaultState())
    onDiscard()
  }, [onDiscard])

  useEffect(() => {
    if (blocker.state === 'blocked') {
      actionBarRef.current?.wiggle({ errorHighlight: true })
      blocker.reset?.()
    }
  }, [blocker])

  return (
    <F0ActionBar
      ref={actionBarRef}
      isOpen={isDirty || barState.status === 'success'}
      variant="light"
      status={barState.status}
      primaryActions={[
        { label: i18n.t('forms.action_bar.save'), onClick: handleSave, disabled: isThinking },
      ]}
      secondaryActions={[
        { label: i18n.t('forms.action_bar.discard'), onClick: handleDiscard, disabled: isThinking },
      ]}
      label={barState.label}
    />
  )
}
